import React from 'react'
// Match whichever build antd itself resolves to at runtime. antd ships both an
// ESM (`es/`) and a CJS (`lib/`) copy, each with its OWN `createContext` call:
// the browser (and this repo's bundler, per `vitest.config.mts`'s own `antd`
// alias comment) takes antd's `module` field, i.e. `es/`, so `Layout.Sider`
// reached via the `src/antd` barrel's `export * from 'antd'` is the `es/`
// component — importing this context from `lib/` would read a DIFFERENT
// context object and silently see the default forever (the same trap
// `src/antd/Input.shadcn.tsx` documents for `DisabledContext`, which also
// imports from `es/`).
import { SiderContext } from 'antd/es/layout/Sider'
import type { MenuProps as AntdMenuProps } from 'antd/es/menu'

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from 'src/components/ui/popover'
import { RightOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import { useInsideOverlaySurface } from './overlay/overlaySurface'

/**
 * Menu, rebuilt on top of a hand-rolled `role="menu"` tree — shadcn has no
 * primitive that covers antd's shape (its own menu-shaped pieces are Radix
 * *popup* menus, not a standing navigation list that can also render inline).
 * Ported from the `update-design` reference lineage's `phase 2 slice 8`
 * (`fm/phase2-shadcn-slice8` and later), reimplemented standalone here since
 * `update-design` is reference-only and never a merge target for this branch.
 *
 * Two families of call site drive the shape:
 *
 *   - **The sidebar** (`components/layout/sidebar/.../NavigationMenu`):
 *     `mode="inline"` with `items`/`selectedKeys`/`openKeys`/`onOpenChange`,
 *     and — the subtle part — an ICON-ONLY collapsed rail whose submenus open
 *     as hover popups. antd got the collapsed flag from `Layout.Sider`
 *     through `SiderContext`, never as a prop (`Layout.Sider` stays antd
 *     until a future layout slice), so this seam reads the same context and
 *     `NavigationMenu` needs no prop change.
 *   - **Dropdown overlays** (top-bar Help/Notifications, `ActionButtons`,
 *     RichTextEditor `Variables`/`FontSize`, waste-fraction `IconSelect`): a
 *     flat vertical list inside the (already Radix, on this branch: still
 *     antd) `Dropdown`'s `dropdownRender`.
 *
 * ITEM STYLING IS BUILT IN, not layered on afterward (survey cause S5: the
 * previous antd-only seam carried none, so every non-sidebar Menu rendered
 * unpadded once ported). Every item/submenu-title below carries its own
 * padding, height, radius and hover/selected paint as Tailwind classes
 * applied DIRECTLY to the element — not via an external stylesheet reaching
 * in with a descendant selector. That is also what fixes the collapsed rail's
 * portalled flyout: `PopupSubmenu`'s items are the exact same
 * `MenuItemRow`/`SubmenuTitle` components as the inline tree, so their
 * classes travel with them into the Radix `Popover.Portal` — a portal moves a
 * node's DOM PARENT, never its React props, so styling baked into the
 * component reaches the portal for free. A `[data-slot='menu-item']`
 * ancestor-scoped rule could never do this (that is the documented cause of
 * the collapsed rail's pre-existing missing hover/selected paint).
 *
 * COLORS AND GEOMETRY COME IN TWO SETS, and the `sidebar` prop (set only by
 * `NavigationMenu`) is what picks between them — see `dropdownPaint` and
 * `sidebarPaint`/`SIDEBAR_ROW`, each of which carries its own reasoning.
 *
 * Every Menu OUTSIDE the sidebar uses the theme-safe SEMANTIC `--wh-*` vars
 * (`--wh-text-primary`, `--wh-blue-100`, `--wh-blue-200`,
 * `--wh-text-dangerous`), not `getMenuThemeConfig`'s raw `navy900`
 * literal: `navy900` is NOT dark-ramped (kept byte-identical across themes on
 * purpose, see `src/styles/palette.ts`), so it reads as near-invisible text
 * on a dark background. `--wh-blue-100`/`--wh-blue-200` ARE properly
 * dark-adapted (regenerated per theme by the same `@ant-design/colors`
 * generator antd's own dark tokens use), so the hover/selected fill stays
 * correct in both themes without a manual light/dark branch — unlike the
 * antd-only `getMenuThemeConfig`, which exists solely to feed antd's
 * `ConfigProvider` and is irrelevant to this branch (that config keeps
 * driving `./Menu.antd` while the toggle is off).
 *
 * The SIDEBAR instead carries the treatment extracted from the captain's own
 * sidebar iterations on `preview/design-toggle-all` — the shadcn token set on
 * a `--muted` sider. `SIDEBAR_ROW` and `sidebarPaint` are where that is
 * sourced and argued; `danger` is the one paint the two sets share, and no
 * sidebar item uses it. The sidebar's row also lets the item's own `w-full`
 * fill the list's `px-2` inset — no external `width: auto !important` fight
 * is needed, because the width is the component's own intrinsic Tailwind
 * class rather than an antd default an external sheet had to override.
 *
 * DOM contract (`.ant-menu*` is deliberately NOT reused):
 *   data-slot="menu"                 root            (was `.ant-menu`)
 *   data-slot="menu-item"            leaf item       (was `.ant-menu-item`)
 *   data-slot="menu-submenu"         a group         (was `.ant-menu-submenu-{inline,vertical}`)
 *   data-slot="menu-submenu-title"   its header      (was `.ant-menu-submenu-title`)
 *   data-slot="menu-submenu-popup"   collapsed popup (was `.ant-menu-submenu-popup`)
 *   data-slot="menu-item-icon" / "menu-title-content" / "menu-item-group-title"
 *   data-slot="menu-divider"
 * plus `data-mode` (inline|vertical|horizontal), `data-collapsed` (replacing
 * `.ant-menu-inline-collapsed`), and `data-selected` / `data-open` /
 * `data-disabled` / `data-danger` on items — `data-selected` on a submenu
 * replaces `.ant-menu-submenu-selected`.
 *
 * Parity points that are easy to lose:
 * - `onClick` receives antd's info object `{ key, keyPath, item, domEvent }`,
 *   not the raw event — `BreadCrumb` does `({ key }) => goBack(+key + 1)`.
 * - An item's own `onClick` fires BEFORE the menu-level `onClick`, as in antd.
 * - `openKeys` is optional: when it is `undefined` the submenu open state is
 *   internal. The sidebar deliberately hands `undefined` while collapsed
 *   (PRO-220) so the hover popups are not pinned by controlled state.
 * - Unknown item props are NOT spread onto the DOM (antd did spread them,
 *   which is why `NavigationMenu` strips `link`/`skipLinks` first — that strip
 *   stays correct either way).
 * - A call site that renders arbitrary `children` instead of `items`
 *   (`InputSliderDropdown`, which wraps a slider panel in `<Menu>` purely to
 *   borrow the dropdown's chrome) is passed through unstyled rather than
 *   dropped: the reference lineage's version ignores `children` entirely,
 *   which would silently blank that panel the moment the toggle flips.
 * - `selectable` defaults to FALSE, not to antd's bare `true`. Five of the
 *   eight call sites host the menu in a `Dropdown`'s `dropdownRender`, where
 *   antd's own `OverrideProvider` forced `selectable: false` on the menu — so
 *   `false` is what those sites have always rendered with. A site that wants a
 *   persistent selected pill opts back in explicitly (`FontSize` does). The
 *   same override also supplied `onClick: () => setOpen(false)`; this seam
 *   deliberately does NOT read antd's `OverrideContext` (antd marks it
 *   `@internal` and the package is scheduled for removal, see
 *   `fe-drop-antd-when-migrated`), so each dropdown-hosted call site closes its
 *   own `Dropdown` from the menu's `onClick` instead.
 *
 * Accepted as UNPORTED — this is the AUTHORITATIVE list, and what a reviewer
 * (and the PR body) should be able to read off in one place:
 * - The collapsed rail's flyout does not close when a child is clicked; antd's
 *   submenu popup did. It self-heals through the existing 100 ms mouse-leave
 *   grace timer as soon as the pointer leaves the popup.
 * - No roving tabindex and no arrow-key/Escape navigation inside
 *   `role="menu"`: rc-menu made the whole menu one tab stop plus arrows, while
 *   here every row is an independently tabbable `<button>`.
 */

/**
 * The collapsed flag exactly as antd's `Layout.Sider` publishes it.
 *
 * This seam reads the Sider's own context rather than a prop (see the header),
 * so anything standing IN for the Sider has to publish it — the app shell's
 * evidence page renders the column without one, and without this it measured
 * the EXPANDED menu inside a 64px rail. Re-exported from here so that stand-in
 * needs no raw `antd/es/...` import of its own: `src/antd` is the seam, and
 * `__tests__/seam-adoption.evidence.test.tsx` counts every file outside it
 * that names the package.
 */
export const SiderCollapsedProvider = SiderContext.Provider

export type MenuProps = AntdMenuProps & {
  /**
   * Structural skin. Set only by the sidebar's `NavigationMenu` — every other
   * call site gets the default flat-list sizing. See the header comment:
   * this affects layout only, never color.
   */
  sidebar?: boolean
  /**
   * Whether the root paints its own floating panel (background, border,
   * radius, shadow).
   *
   * The seam OWNS this surface rather than inheriting one. antd's own
   * `.ant-menu` vertical root paints only `colorBgContainer` — the panel a
   * dropdown-hosted menu appears to sit on comes from
   * `.ant-dropdown .ant-dropdown-menu`, a prefixCls swap antd performs
   * internally and this hand-rolled component cannot replicate. So the seam
   * renders the finished appearance itself, as the other ported seams do.
   *
   * Defaults to on for a free-standing `vertical` menu and off for the
   * sidebar's `inline` tree and for a `horizontal` bar, neither of which
   * floats — and off inside a seam that already paints a panel, which
   * `./overlay/overlaySurface` reports (that is what the `dropdownRender` call
   * sites hit). Pass `false` explicitly at a call site that nests the menu
   * inside some OTHER panel — see `IconSelect`, which renders it inside a
   * Select dropdown.
   */
  surface?: boolean
}

type AnyItem = {
  key?: React.Key
  type?: 'group' | 'divider' | 'item' | 'submenu'
  label?: React.ReactNode
  icon?: React.ReactNode
  disabled?: boolean
  danger?: boolean
  title?: string
  children?: AnyItem[]
  onClick?: (info: MenuClickInfo) => void
  className?: string
  style?: React.CSSProperties
}

type MenuClickInfo = {
  key: string
  keyPath: string[]
  item: React.ReactInstance | null
  domEvent: React.MouseEvent | React.KeyboardEvent
}

/**
 * `rest` is whatever of antd's `MenuProps` the destructuring above did not
 * name, and it lands on a real `<ul>`. Keep only what the DOM understands so a
 * future call site passing an antd-only prop (`prefixCls`, `rootClassName`,
 * `getPopupContainer`, `disabledOverflow`, `activeKey`, `motion`, …) cannot
 * leak an unknown-prop warning.
 */
const DOM_SAFE_PROPS = new Set([
  'id',
  'title',
  'tabIndex',
  'lang',
  'dir',
  'hidden',
  'role',
])

const domSafeProps = (rest: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(rest).filter(
      ([key]) =>
        key.startsWith('data-') ||
        key.startsWith('aria-') ||
        DOM_SAFE_PROPS.has(key) ||
        /^on[A-Z]/.test(key)
    )
  )

const isSubmenu = (item: AnyItem) =>
  Array.isArray(item.children) && item.type !== 'group'

/**
 * antd wraps an inline-collapsed item in a Tooltip carrying its label, so an
 * icon-only rail row still has a name. `getItems` only sets `title` on CHILD
 * items, so fall back to the label whenever it is plain text.
 */
const accessibleName = (item: AnyItem): string | undefined =>
  item.title ?? (typeof item.label === 'string' ? item.label : undefined)

/**
 * An icon's SIZE is an antd Menu COMPONENT TOKEN, so it does not survive the
 * port on its own — this is the seam carrying the token itself.
 *
 * antd writes `min-width`/`font-size: iconSize` onto every
 * `.ant-menu-item-icon` (`antd/es/menu/style/index.js`) and, for a TOP-LEVEL
 * row of an inline-collapsed root only, `font-size: collapsedIconSize`
 * (`antd/es/menu/style/vertical.js`, matched with a direct-child `>` — a row
 * inside a flyout keeps `iconSize`, which is why `MenuItemRow` is handed
 * `collapsed && depth === 0`). Both tokens reach antd's `ConfigProvider` and
 * nothing else, so with the toggle ON the glyph fell back to its intrinsic
 * `1em`, i.e. the row's inherited 14px — measured 14×14 against antd's 18×18
 * expanded and 20×20 on the rail.
 *
 * Every Menu OUTSIDE the sidebar keeps antd's own defaults (`iconSize:
 * fontSize` = 14, `collapsedIconSize: fontSizeLG` = 16), so no dropdown-hosted
 * row moves. Written as a real `font-size` rather than a Tailwind class for
 * the reason the file header gives about the flyout: it travels with the
 * component into the Radix portal, and it is a value a test can read back
 * rather than a class name it has to trust.
 *
 * THE SIDEBAR'S TWO NUMBERS MIRROR `MENU_STRUCTURAL` (`iconSize: 18`,
 * `collapsedIconSize: 20`) — restated here so a port cannot silently drop
 * them, and RESTORED after PRO-736.14 read them off the captain's frame at
 * 1:1 and shipped 24/24.
 *
 * THE SCALE ERROR, named so it cannot be repeated (PRO-736.15, the captain:
 * "the icons are huge i think thats the main problem"). The frame is a
 * 364px-wide artboard; this column is 256px (`SIDER_WIDTH`), i.e. 0.703x. A
 * frame value taken at 1:1 therefore lands ~42% oversized: its 24px glyph
 * scales to 16.9px, its 48px row to 33.7px, its 16px inner padding and
 * icon->label gap to 11.2px each. The values the sidebar ALREADY had —
 * 18px glyph, 36px row, a 40px pitch — are within a few percent of that, and
 * they were reached by the captain's own seventeen iterations IN the 256px
 * column rather than derived from the artboard. So the frame is read for
 * INTENT and the column's own measured rhythm wins on numbers.
 * `__tests__/menu-icon-size.test.tsx` holds both branches; the sidebar and
 * `MENU_STRUCTURAL` now agree again, so a drift on either side is a real
 * decision rather than an accident.
 */
const MENU_ICON_SIZE = {
  default: { expanded: 14, collapsed: 16 },
  sidebar: { expanded: 18, collapsed: 20 },
}

export const menuIconSize = ({
  collapsed,
  sidebar,
}: {
  collapsed?: boolean
  sidebar?: boolean
}): number =>
  MENU_ICON_SIZE[sidebar ? 'sidebar' : 'default'][
    collapsed ? 'collapsed' : 'expanded'
  ]

/**
 * The SIDEBAR row.
 *
 * THE COLOURS came from the captain's own sidebar iterations on
 * `preview/design-toggle-all` (commits "chore(preview): sidebar fix 1-17"),
 * where the nav was still antd and they were delivered by the `dashboard-01`
 * experiment's `SidebarStyle` sheet. `#1006` moved the sidebar onto this seam,
 * at which point every one of those `.ant-menu-*` rules stopped matching and
 * the nav fell back to the dropdown paint — which is the drift `sidebarPaint`
 * restates in the component, where a portal and a stylesheet can both reach
 * it. They were MEASURED rather than read off the diff (the sheet's
 * unprefixed declarations lose to antd's four-class cssinjs selectors, so two
 * of them never rendered at all; the hover is carried at its AUTHORED 6% wash
 * rather than at antd's blue100/navy900).
 *
 * THE GEOMETRY IS THEIRS AGAIN (PRO-736.15). PRO-736.14 replaced it with the
 * captain's sidebar frame read at 1:1 — 48px rows on a 56px pitch, 16px inner
 * padding, a 24px glyph, a 56px label column. That artboard is 364px wide and
 * this column is 256px, so every one of those numbers arrived ~42% oversized
 * (see `MENU_ICON_SIZE` above for the arithmetic), which is what the captain
 * saw as "the icons are huge". The preview's rhythm — 36px rows on a 40px
 * pitch, a 24px icon inset, an 18px glyph, a 52px label column — is the
 * SCALED frame to within a few percent AND was measured in this column, so it
 * is what the column carries. The frame remains the authority on INTENT (the
 * company name at the top, a flat search field, no email line, no chevron,
 * one shared left edge, tokens throughout); the preview commits remain the
 * authority on the colours and on these numbers.
 *
 * Everything else in the column is built on them: the shell's brand, search,
 * utility and user blocks all take them through `SIDEBAR_ROW_METRICS`, so a
 * second left edge cannot appear.
 */
const SIDEBAR_ROW = {
  /** fix 13 — 36px rows, 14px/400 type. Was `h-12` for one release
   *  (PRO-736.14's unscaled 48). */
  height: 'h-9',
  /** MEASURED as 8px and kept as a literal by the theme-preset pass: the
   *  token is 7.2px (`--radius`), and `navRow.ts` records the measurement. */
  radius: 'rounded-[8px]',
  /** navRow: icon 18px at 24px into the row, label at 24 + 18 + 10 = 52. */
  gap: 'gap-2.5',
  padding: 'pl-6 pr-3',
  /** THE NESTED ROW, and the second half of the defect PRO-736.15 repairs.
   *
   *  The real navigation has expandable groups whose CHILDREN CARRY THEIR OWN
   *  ICONS (Intelligence over Monitor / Ask / Report / Trust / Data &
   *  Analytics — see `NavigationMenu/getItems.tsx`). Both previous values
   *  were written for a child that has none: antd's own 48px inline indent
   *  put a child's glyph 4px shy of its parent's LABEL, and PRO-736.14's
   *  56px put it on the parent's label column but its label 40px further
   *  right again, on nothing. Either way the group read as four ragged left
   *  edges.
   *
   *  So the child's GLYPH is aligned to the parent's LABEL column (24 + 18 +
   *  10 = 52) and its label follows its own glyph. That is two edges for the
   *  whole tree — parent glyphs at 24, parent labels and child glyphs at 52 —
   *  and one clean indent step, which is what makes a group read as a group.
   *  Verified against the REAL menu, not a flat list: see
   *  `components/main/shell/__tests__/shell-sidebar.evidence.test.tsx`. */
  nestedPadding: 'pl-13 pr-3',
  /** THE RAIL'S ONE OPTICAL COLUMN. antd put its collapsed glyph 16px into a
   *  48px rail row, i.e. 2px right of centre, and this seam reproduced that —
   *  which left the brand mark above it (centred on the rail) and every glyph
   *  below it disagreeing about where the rail's centre is. Centred instead,
   *  so the mark and the glyphs share one axis; `collapsedIconInset` below is
   *  the same placement as a NUMBER, for the two shell blocks that need one. */
  collapsedPadding: 'justify-center px-0',
} as const

/**
 * The same row, as NUMBERS.
 *
 * The app shell's lower sidebar group (Notifications / Get Help / Settings,
 * `components/main/shell/SidebarUtilityGroup.tsx`) is made of controls this
 * seam does not own — each is a real component with its own trigger, so its
 * trigger is stretched over the row rather than the row wrapping it in a new
 * button. That stretch is a CSS rule, and a CSS rule needs a length, not a
 * Tailwind class. Restating the numbers there is what let the preview
 * branch's lower group drift 8px off the nav column twice, so they live here
 * beside the classes they mirror instead.
 *
 * `inset`/`collapsedWidth` describe the LIST rather than the row: the sidebar
 * `<ul>` carries `px-2` and, collapsed, `w-16`, so a `w-full` row is 240px in
 * the 256px column and 48px on the 64px rail.
 *
 * `__tests__/menu-sidebar-row-metrics.test.ts` holds each field against the
 * utility it mirrors — a class and a number cannot be kept in step by reading.
 */
export const SIDEBAR_ROW_METRICS = {
  /** the list's `px-2` */
  inset: 8,
  /** `h-9` */
  height: 36,
  /** `rounded-[8px]` */
  radius: 8,
  /** `pl-6` — distance from the row's left edge to the glyph's */
  iconInset: 24,
  /** `pr-3` — the row's own trailing padding */
  paddingEnd: 12,
  /**
   * `justify-center` on the collapsed rail, as a NUMBER — the two shell
   * blocks that stretch a foreign control over the row can only place it with
   * a length. DERIVED rather than written down so it cannot drift from the
   * glyph it centres: the rail row is `collapsedWidth` less the list's inset
   * on both edges, and the glyph is `MENU_ICON_SIZE.sidebar.collapsed`.
   */
  collapsedIconInset: (64 - 8 * 2 - MENU_ICON_SIZE.sidebar.collapsed) / 2,
  /** `gap-2.5` */
  iconLabelGap: 10,
  /** the list's `gap-1` — the preview's 40px pitch is 36 + this */
  rowGap: 4,
  /**
   * The gap at a BOUNDARY BETWEEN BLOCKS, as opposed to `rowGap` between rows
   * of one list.
   *
   * The column is one list on one rhythm, which is what makes it read as a
   * list — but the search row is not a nav item, and on the row rhythm it read
   * as one (the captain, 2026-08-26). A boundary is a multiple of the same
   * unit rather than a number of its own, so nothing here can drift off the
   * grid: `rowGap * 4`.
   *
   * Applied at exactly one boundary today, search → nav
   * (`components/main/shell/SidebarSearchRow`). The nav → utility-group and
   * utility-group → user boundaries are deliberately left on `rowGap`: those
   * three ARE rows of the same kind and separating them would break the one
   * list the column is.
   */
  blockGap: 4 * 4,
  /**
   * A nested row's own inset: `pl-13`, which is `iconInset + the glyph +
   * iconLabelGap`, i.e. the parent's LABEL column. See `SIDEBAR_ROW`'s
   * `nestedPadding` for why the child's GLYPH is what lands there.
   */
  nestedIconInset: 24 + MENU_ICON_SIZE.sidebar.expanded + 10,
  /** the list's `w-16` */
  collapsedWidth: 64,
} as const

/**
 * Every Menu OUTSIDE the sidebar — the dropdown-hosted lists. Unchanged since
 * the port: `--wh-text-primary` on a `--wh-blue-100` hover and a
 * `--wh-blue-200` selected fill, which is what antd's own `ConfigProvider`
 * tokens resolve to for those call sites.
 */
const dropdownPaint = ({
  selected,
  disabled,
  danger,
}: {
  selected: boolean
  disabled?: boolean
  danger?: boolean
}) =>
  cn(
    'text-[var(--wh-text-primary)]',
    !disabled && !selected && !danger && 'hover:bg-[var(--wh-blue-100)]',
    selected && 'font-medium',
    selected && !danger && 'bg-[var(--wh-blue-200)]'
  )

/**
 * The SIDEBAR column's paint — the shadcn token set, not `--wh-*`.
 *
 * The sider itself takes `--muted` with the toggle on (sidebar fix 17, see
 * `components/layout/sidebar/index.tsx`), so the selected row's `--background`
 * fill is the one raised surface in the column. On the white `bgSider` this
 * seam used to sit on, that pill would be invisible — the two changes are a
 * pair and neither works alone.
 *
 * `selectionStyle: 'text'` is the group-header case: an EXPANDED group whose
 * child is selected recolours its title only, so the one raised pill is the
 * row the reader is actually on (preview fix 4, "one quiet active signal, not
 * three stacked"). The collapsed rail keeps the pill on its group header,
 * because there no leaf row is rendered to carry it.
 */
const sidebarPaint = ({
  selected,
  disabled,
  danger,
  selectionStyle,
}: {
  selected: boolean
  disabled?: boolean
  danger?: boolean
  selectionStyle: 'pill' | 'text'
}) =>
  cn(
    !selected && 'text-muted-foreground',
    !disabled &&
      !selected &&
      !danger &&
      cn(
        'hover:bg-[color-mix(in_oklab,var(--foreground)_6%,transparent)]',
        // `--sidebar-accent-foreground` rather than `--foreground`: the
        // captain's Figma frame (node 1:5) specifies `#18181b` for a selected
        // row and this token resolves to exactly that — measured,
        // canvas-sampled, not assumed. It is also the semantically right one,
        // since `--sidebar-accent` IS the `#f4f4f5` surface these rows sit on.
        // In dark both it and `--foreground` resolve to `#fafafa`, so nothing
        // diverges there. Both states are written out in full because Tailwind
        // extracts whole class candidates from source text — an interpolated
        // variant is never compiled.
        'hover:text-sidebar-accent-foreground'
      ),
    selected && cn('font-medium', 'text-sidebar-accent-foreground'),
    selected &&
      !danger &&
      selectionStyle === 'pill' &&
      'bg-background shadow-[0_1px_2px_0_color-mix(in_oklab,var(--foreground)_8%,transparent)]'
  )

/** Shared by the inline tree AND the collapsed rail's popup — see the header. */
const itemClasses = ({
  selected,
  disabled,
  danger,
  collapsed,
  sidebar,
  depth = 0,
  selectionStyle = 'pill',
}: {
  selected: boolean
  disabled?: boolean
  danger?: boolean
  collapsed?: boolean
  sidebar?: boolean
  depth?: number
  selectionStyle?: 'pill' | 'text'
}) =>
  cn(
    'flex w-full items-center text-left text-sm transition-colors',
    !disabled && 'cursor-pointer',
    sidebar
      ? cn(
          SIDEBAR_ROW.height,
          SIDEBAR_ROW.radius,
          SIDEBAR_ROW.gap,
          // eslint-disable-next-line no-nested-ternary
          collapsed
            ? SIDEBAR_ROW.collapsedPadding
            : depth === 0
            ? SIDEBAR_ROW.padding
            : SIDEBAR_ROW.nestedPadding,
          sidebarPaint({ selected, disabled, danger, selectionStyle })
        )
      : cn(
          'gap-2 rounded-md px-3 py-2',
          collapsed && 'justify-center px-0',
          dropdownPaint({ selected, disabled, danger })
        ),
    disabled && 'pointer-events-none cursor-not-allowed opacity-50',
    danger &&
      cn(
        'text-[var(--wh-text-dangerous)]',
        selected && 'bg-[var(--wh-text-dangerous)]/10',
        !disabled && !selected && 'hover:bg-[var(--wh-text-dangerous)]/10'
      )
  )

/**
 * The SIDEBAR row's classes, for a row this seam does not render.
 *
 * The shell's lower sidebar group and its user block sit in the same column as
 * the nav and must read as the same list — same height, radius, icon column,
 * label offset, idle colour, hover wash and selected pill. They are not menu
 * items (each wraps a real top-bar control, or is the account menu's trigger),
 * so they cannot go through `items`; this is the one supported way to take the
 * treatment without restating it. Everything it returns is `itemClasses` with
 * `sidebar: true` — there is no second definition to drift.
 */
export const sidebarRowClasses = ({
  selected = false,
  collapsed = false,
  nested = false,
}: {
  selected?: boolean
  collapsed?: boolean
  /** A row one level inside a group. No shell block renders one today; it is
   *  here so the nested indent has exactly one definition, which
   *  `__tests__/menu-sidebar-row-metrics.test.ts` reads back. */
  nested?: boolean
} = {}): string =>
  itemClasses({ selected, collapsed, sidebar: true, depth: nested ? 1 : 0 })

const MenuItemRow = ({
  item,
  depth,
  indentDepth = depth,
  selected,
  collapsed,
  sidebar,
  onSelect,
}: {
  item: AnyItem
  depth: number
  /** Depth the ROW's indent is drawn from — 0 inside a popup. See renderItems. */
  indentDepth?: number
  selected: boolean
  collapsed: boolean
  sidebar?: boolean
  onSelect: (
    item: AnyItem,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void
}) => {
  const name = accessibleName(item)

  return (
    <li role="none" className={item.className} style={item.style}>
      <button
        type="button"
        role="menuitem"
        data-slot="menu-item"
        data-selected={selected ? 'true' : undefined}
        data-disabled={item.disabled ? 'true' : undefined}
        data-danger={item.danger ? 'true' : undefined}
        data-depth={depth}
        disabled={item.disabled}
        title={collapsed ? name : item.title}
        aria-label={collapsed ? name : undefined}
        aria-current={selected && sidebar ? 'page' : undefined}
        className={itemClasses({
          selected,
          disabled: item.disabled,
          danger: item.danger,
          collapsed,
          sidebar,
          depth: indentDepth,
        })}
        onClick={(event) => onSelect(item, event)}
      >
        {item.icon != null && (
          <span
            data-slot="menu-item-icon"
            className="flex shrink-0 items-center"
            style={{ fontSize: menuIconSize({ collapsed, sidebar }) }}
          >
            {item.icon}
          </span>
        )}
        {!collapsed && (
          <span
            data-slot="menu-title-content"
            className="min-w-0 flex-1 truncate"
          >
            {item.label}
          </span>
        )}
      </button>
    </li>
  )
}

/**
 * A submenu in the collapsed rail: antd rendered these as hover popups beside
 * the rail. The popup is anchored on the rail row and driven by pointer
 * enter/leave rather than click, matching that behaviour.
 */
const PopupSubmenu = ({
  item,
  selected,
  sidebar,
  renderChildren,
}: {
  item: AnyItem
  selected: boolean
  sidebar?: boolean
  renderChildren: () => React.ReactNode
}) => {
  const [open, setOpen] = React.useState(false)
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>()

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }
  // A small grace period: the pointer has to cross the gap between the rail
  // and the popup, and antd's popups tolerate that too.
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), 100)
  }
  React.useEffect(() => cancelClose, [])

  return (
    <li
      role="none"
      data-slot="menu-submenu"
      data-selected={selected ? 'true' : undefined}
      data-open={open ? 'true' : undefined}
      data-mode="vertical"
      className={item.className}
      style={item.style}
      onMouseEnter={() => {
        cancelClose()
        setOpen(true)
      }}
      onMouseLeave={scheduleClose}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <button
            type="button"
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            data-slot="menu-submenu-title"
            data-selected={selected ? 'true' : undefined}
            data-disabled={item.disabled ? 'true' : undefined}
            data-danger={item.danger ? 'true' : undefined}
            disabled={item.disabled}
            title={accessibleName(item)}
            aria-label={accessibleName(item)}
            className={itemClasses({
              selected,
              disabled: item.disabled,
              danger: item.danger,
              collapsed: true,
              sidebar,
            })}
          >
            {item.icon != null && (
              <span
                data-slot="menu-item-icon"
                className="flex items-center"
                style={{
                  fontSize: menuIconSize({ collapsed: true, sidebar }),
                }}
              >
                {item.icon}
              </span>
            )}
          </button>
        </PopoverAnchor>
        <PopoverContent
          data-slot="menu-submenu-popup"
          side="right"
          align="start"
          sideOffset={4}
          // The rail row keeps the pointer-driven open state; letting Radix
          // steal focus on open would close it again the moment the pointer
          // moves back to the rail.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="min-w-[200px] bg-[var(--wh-gray-200)] p-1.5"
        >
          <ul
            role="menu"
            data-slot="menu"
            data-mode="vertical"
            className="m-0 flex list-none flex-col gap-0.5 p-0"
          >
            {renderChildren()}
          </ul>
        </PopoverContent>
      </Popover>
    </li>
  )
}

const MenuInner = (
  {
    items,
    mode = 'vertical',
    selectedKeys,
    defaultSelectedKeys,
    openKeys,
    defaultOpenKeys,
    onOpenChange,
    onClick,
    selectable = false,
    inlineCollapsed,
    sidebar,
    surface,
    className,
    style,
    children,
    theme: _theme,
    expandIcon: _expandIcon,
    triggerSubMenuAction: _triggerSubMenuAction,
    subMenuCloseDelay: _subMenuCloseDelay,
    subMenuOpenDelay: _subMenuOpenDelay,
    forceSubMenuRender: _forceSubMenuRender,
    overflowedIndicator: _overflowedIndicator,
    inlineIndent: _inlineIndent,
    multiple: _multiple,
    onSelect: onSelectProp,
    onDeselect: _onDeselect,
    ...rest
  }: MenuProps,
  ref: React.Ref<HTMLUListElement>
): JSX.Element => {
  // `Layout.Sider` is still antd and drives the collapsed rail through this
  // context, exactly as antd's own Menu reads it.
  const { siderCollapsed } = React.useContext(SiderContext)
  const collapsed =
    mode === 'inline' && (siderCollapsed ?? inlineCollapsed ?? false)
  // A menu handed to `src/antd/Dropdown` through `dropdownRender` is already
  // sitting on that seam's panel; painting a second one is the double-wrap
  // defect. An explicit `surface` still wins.
  const insideOverlayPanel = useInsideOverlaySurface()
  const hasSurface =
    surface ?? (mode === 'vertical' && !sidebar && !insideOverlayPanel)

  const [innerSelected, setInnerSelected] = React.useState<string[]>(() =>
    (defaultSelectedKeys ?? []).map(String)
  )
  const selected = selectedKeys ? selectedKeys.map(String) : innerSelected

  const [innerOpen, setInnerOpen] = React.useState<string[]>(() =>
    (defaultOpenKeys ?? []).map(String)
  )
  const openControlled = openKeys !== undefined
  const open = openControlled ? (openKeys ?? []).map(String) : innerOpen

  const setOpen = (next: string[]) => {
    if (!openControlled) setInnerOpen(next)
    onOpenChange?.(next)
  }

  const handleSelect = (
    item: AnyItem,
    event: React.MouseEvent | React.KeyboardEvent,
    keyPath: string[]
  ) => {
    if (item.disabled) return
    const key = String(item.key)
    const info: MenuClickInfo = { key, keyPath, item: null, domEvent: event }
    // antd runs the item's own handler first, then the menu-level one.
    item.onClick?.(info)
    onClick?.(info as never)
    if (selectable) {
      if (!selectedKeys) setInnerSelected([key])
      onSelectProp?.({ ...info, selectedKeys: [key] } as never)
    }
  }

  const renderItems = (
    list: AnyItem[],
    depth: number,
    parents: string[],
    // The collapsed rail's flyout is a POPUP: antd stops indenting by level
    // there (`.ant-menu-vertical` resets to `itemPaddingInline`), because the
    // parent row is not on screen to indent away from. `depth` still describes
    // the tree — it is what `data-depth` reports — so the flag is separate
    // rather than folded into it.
    popup = false
  ): React.ReactNode =>
    list.filter(Boolean).map((item, index) => {
      const key = String(item.key ?? index)

      if (item.type === 'divider') {
        return (
          <li
            key={`divider-${key}`}
            role="separator"
            data-slot="menu-divider"
            className="my-1 h-px bg-[var(--wh-gray-400)]"
          />
        )
      }

      if (item.type === 'group') {
        return (
          <li key={`group-${key}`} role="none" data-slot="menu-item-group">
            {item.label != null && (
              <div
                data-slot="menu-item-group-title"
                className="px-3 pt-2 pb-1 text-xs text-[var(--wh-text-secondary)]"
              >
                {item.label}
              </div>
            )}
            <ul
              role="group"
              data-slot="menu-item-group-list"
              className="m-0 list-none p-0"
            >
              {renderItems(item.children ?? [], depth, parents, popup)}
            </ul>
          </li>
        )
      }

      if (isSubmenu(item)) {
        const childKeys = (item.children ?? []).map((child) =>
          String(child.key)
        )
        const hasSelectedChild = childKeys.some((child) =>
          selected.includes(child)
        )
        const isOpen = open.includes(key)

        if (collapsed) {
          return (
            <PopupSubmenu
              key={key}
              item={item}
              sidebar={sidebar}
              selected={hasSelectedChild || selected.includes(key)}
              renderChildren={() =>
                renderItems(
                  item.children ?? [],
                  depth + 1,
                  [...parents, key],
                  true
                )
              }
            />
          )
        }

        return (
          <li
            key={key}
            role="none"
            data-slot="menu-submenu"
            data-mode={mode}
            data-open={isOpen ? 'true' : undefined}
            data-selected={hasSelectedChild ? 'true' : undefined}
            className={item.className}
            style={item.style}
          >
            <button
              type="button"
              role="menuitem"
              aria-haspopup="true"
              aria-expanded={isOpen}
              data-slot="menu-submenu-title"
              data-selected={hasSelectedChild ? 'true' : undefined}
              disabled={item.disabled}
              className={cn(
                itemClasses({
                  selected: hasSelectedChild,
                  disabled: item.disabled,
                  sidebar,
                  depth,
                  // Expanded, the selected CHILD below carries the raised
                  // pill, so the group header recolours only — one active
                  // signal per column rather than two rows lit at once
                  // (preview sidebar fix 4). Read by `sidebarPaint` alone, so
                  // no dropdown-hosted submenu title moves.
                  selectionStyle: 'text',
                }),
                'justify-between'
              )}
              onClick={() =>
                setOpen(
                  isOpen
                    ? open.filter((openKey) => openKey !== key)
                    : [...open, key]
                )
              }
            >
              <span
                className={cn(
                  'flex min-w-0 flex-1 items-center',
                  // A group header is a row like any other, so its glyph and
                  // label sit on the same 16px gap the leaf rows use and its
                  // label lands on the one 56px label column. Outside the
                  // sidebar this stays antd's own 8px — no dropdown-hosted
                  // menu moves.
                  sidebar ? SIDEBAR_ROW.gap : 'gap-2'
                )}
              >
                {item.icon != null && (
                  <span
                    data-slot="menu-item-icon"
                    className="flex shrink-0 items-center"
                    // This branch is the EXPANDED submenu title — the
                    // collapsed rail returned `PopupSubmenu` above.
                    style={{ fontSize: menuIconSize({ sidebar }) }}
                  >
                    {item.icon}
                  </span>
                )}
                <span
                  data-slot="menu-title-content"
                  className="min-w-0 flex-1 truncate"
                >
                  {item.label}
                </span>
              </span>
              <RightOutlined
                data-slot="menu-submenu-arrow"
                className={cn(
                  'shrink-0 transition-transform',
                  isOpen && 'rotate-90'
                )}
                aria-hidden
              />
            </button>
            {isOpen && (
              <ul
                role="menu"
                data-slot="menu-sub"
                data-mode={mode}
                // The sidebar's nested rows sit on the SAME 40px pitch as the
                // rows around them — this list carries the gap because the
                // outer one only ever sees the whole submenu as one `<li>`,
                // so without it a group's children pack flush while every
                // other row in the column is 4px apart.
                className={cn(
                  'm-0 list-none p-0',
                  sidebar && 'flex flex-col gap-1 pt-1'
                )}
                style={{ paddingInlineStart: sidebar ? 0 : 12 }}
              >
                {renderItems(item.children ?? [], depth + 1, [...parents, key])}
              </ul>
            )}
          </li>
        )
      }

      return (
        <MenuItemRow
          key={key}
          item={item}
          depth={depth}
          indentDepth={popup ? 0 : depth}
          collapsed={collapsed && depth === 0}
          selected={selected.includes(key)}
          sidebar={sidebar}
          onSelect={(clicked, event) =>
            handleSelect(clicked, event, [...parents, key].reverse())
          }
        />
      )
    })

  return (
    <ul
      ref={ref}
      role={mode === 'horizontal' ? 'menubar' : 'menu'}
      data-slot="menu"
      data-mode={mode}
      data-collapsed={collapsed ? 'true' : undefined}
      className={cn(
        'm-0 flex list-none p-0',
        mode === 'horizontal' ? 'flex-row items-center' : 'flex-col gap-0.5',
        hasSurface &&
          'bg-popover text-popover-foreground rounded-md border p-1 shadow-md',
        // `px-2` is preview sidebar fix 5, "ONE inset for every row, top
        // level and nested alike": it sits on the LIST, not the row, so a
        // nested `menu-sub` inherits it and every row lines up on both
        // edges. Put on the row instead it would fight the row's own
        // `w-full` and overflow the rail by 16px — the trap the reference
        // lineage documents at length.
        // `gap-1` is the preview's 4px between rows — with the 36px row above
        // it that is the 40px pitch every group in the column is drawn on. No
        // vertical padding of its own: the shell's blocks above and below each
        // carry their own `rowGap`, and a `py` here would add to it and make
        // the two seams either side of the nav wider than every other gap in
        // the column (it was `py-1.5`, i.e. 14px seams against 4px rows).
        sidebar && 'gap-1 px-2 py-0',
        collapsed && 'w-16',
        className
      )}
      style={style}
      {...domSafeProps(rest as Record<string, unknown>)}
    >
      {items ? renderItems(items as AnyItem[], 0, []) : children}
    </ul>
  )
}

export const Menu = React.forwardRef(MenuInner)

Menu.displayName = 'Menu'

export default Menu
